import React, { useEffect, useRef, useState } from 'react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material'
import { CloudDownload, DeleteOutlined } from '@mui/icons-material'
import GetAppIcon from '@mui/icons-material/GetApp'
import { toast } from 'react-toastify'
import { useUploadFileMutation } from '../../../../store/api/uploadApi'
import { isEmpty } from 'lodash'
import { isPendingAttachment, validateAttachments, isSameAttachmentList } from '../../../../components/Dialog/attachmentValidation'

const SupBankttachmentDialog = ({ module, value, setValue = () => { }, initialData, onlyView, open, setOpen }) => {
  const inputRef = useRef()
  const autoAttachIdRef = useRef(0)
  const [files, setFiles] = useState([])
  const [newFilesToUpload, setNewFilesToUpload] = useState([])


  const [postData, { data: postedData, isLoading: isUploading, isError: uploadError }] = useUploadFileMutation()

  const handleClose = () => {
    if (isUploading) {
      toast.warning('Upload is still in progress. Please wait.')
      return
    }
    // fileLink-i olmayan attachment ile baglamaga icaze vermirik
    if (!validateAttachments((files || []).filter((f) => f.type !== 2))) return

    setOpen(false)
  }


  useEffect(() => {
    if (isEmpty(value)) {
      // value hər render-də yeni boş massiv ola bilər; artıq boşdursa yeni state yaratmırıq
      // (yoxsa setFiles -> setValue -> valideyn state -> yeni value -> setFiles dövrəsi yaranır).
      setFiles((prev) => ((prev || []).length === 0 ? prev || [] : []))
    }
  }, [value])

  useEffect(() => {
    // initialData valideyndə hər render-də yeni massiv kimi yarana bilir.
    // Məzmun eynidirsə state-i dəyişmirik: əks halda
    // setFiles -> setValue -> valideyn state -> yeni initialData -> setFiles ... sonsuz dövrə yaranır.
    const next = initialData || []
    setFiles((prev) => (isSameAttachmentList(prev, next) ? prev : next))
  }, [initialData])

  // yalniz yeni sechilen batch upload olunur (butun siyahi yox)
  useEffect(() => {
    if (newFilesToUpload.length === 0) return

    const formData = new FormData()
    formData.append('module', module)
    newFilesToUpload.forEach((item) => formData.append('files', item.file))

    postData(formData)
  }, [newFilesToUpload])

  useEffect(() => {
    if (newFilesToUpload.length === 0) return
    if (!postedData && !uploadError) return

    // Linkleri gonderilen batch-in sirasina gore uygunlashdiririq.
    const linkByAttachmentId = new Map()
    if (postedData?.message === 'Request succeeded') {
      newFilesToUpload.forEach((item, index) => {
        const link = postedData?.data?.[index]
        if (link) linkByAttachmentId.set(item.attachmentId, link)
      })
    }

    const failed = newFilesToUpload.filter((item) => !linkByAttachmentId.has(item.attachmentId))

    // link almayan fayllari siyahida saxlamiriq ki, fileLink = null save-e getmesin
    const updatedFiles = (files || [])
      .filter((file) => !failed.some((item) => item.attachmentId === file.attachmentId))
      .map((file) =>
        linkByAttachmentId.has(file.attachmentId)
          ? { ...file, fileLink: linkByAttachmentId.get(file.attachmentId) }
          : file
      )

    if (failed.length > 0) {
      toast.error(`Upload failed for: ${failed.map((item) => item.name).join(', ')}. Please try again.`)
    }

    setFiles(updatedFiles)
    setValue(updatedFiles)
    setNewFilesToUpload([])
  }, [postedData, uploadError])


  const handleDownload = async (file) => {
    const link = document.createElement('a')
    link.href = file.fileLink
    link.setAttribute('download', file.name)
    document.body.appendChild(link)
    link.click()
  }

  const handleDelete = (file) => {
    let newFiles = [...files]
    const updatedFile = newFiles.map((item) => {
      if (item.attachmentId === file.attachmentId && file.type !== 2) {
        return { ...item, type: 2 }
      }
      return item
    })
    if (inputRef.current) inputRef.current.value = ''
    setFiles(updatedFile)
    setValue(updatedFile)
  }

  const handleFileUpload = (file) => {
    if (!file) {
      return
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.warning(`File size cannot exceed 2MB `)
      return
    }

    const isDuplicateFile = files?.filter((f) => f.type !== 2).some((existingFile) => existingFile.name === file.name)

    if (isDuplicateFile) {
      toast.warning(`A file with the same name already exists.`)
      return
    }

    autoAttachIdRef.current--

    const newFile = {
      attachmentId: autoAttachIdRef.current,
      name: file.name,
      sourceId: 0,
      sourceTypeId: 0,
      reference: '',
      extensionType: file.type,
      attachmentTypeId: 0,
      attachmentSubTypeId: 0,
      lastModifiedDate: file.lastModifiedDate.toISOString(),
      size: file.size,
      file: file,
      fileLink: '',
      type: 0,
    }

    setFiles((prevFiles) => [...(prevFiles || []), newFile])
    setNewFilesToUpload((prev) => [...prev, newFile])
  }

  const handleFileSelect = (e) => {
    const selected = e.target.files

    if (selected) {
      Array.from(selected).forEach((file) => handleFileUpload(file))
    }
    e.target.value = ''
  }


  return (
    <Dialog open={open} onClose={handleClose} fullWidth>
      <DialogTitle sx={{ backgroundColor: '#1976d2', color: '#fff' }}>Evaluation Form Attachments</DialogTitle>
      <Divider />

      {!onlyView && (
        <div className="uploadArea">
          <Button variant="contained" component="label" endIcon={<CloudDownload />} disabled={isUploading}>
            Upload
            <input
              onChange={handleFileSelect}
              ref={inputRef}
              hidden
              type="file"
              multiple
              accept=".xlsx,.xls,image/*,.doc, .docx,.ppt, .pptx,.txt,.pdf"
            />
          </Button>
        </div>
      )}

      <DialogContent>
        <TableContainer component={Paper}>
          <Table>
            <TableHead variant="head">
              <TableRow>
                <TableCell sx={{ paddingLeft: '.5rem !important' }}>File Name</TableCell>
                <TableCell sx={{ paddingLeft: '.5rem !important' }}>Size</TableCell>
                <TableCell sx={{ paddingLeft: '.5rem !important' }}>Status</TableCell>
                <TableCell sx={{ paddingLeft: '.5rem !important' }} align="right">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {files &&
                files?.filter((f) => f.type !== 2).length > 0 &&
                files
                  ?.filter((f) => f.type !== 2)
                  .map((file, index) => (
                    <TableRow key={index} hover={true}>
                      <TableCell component="th" scope="row" size="small" sx={{ padding: '.5rem !important' }}>
                        {file.name}
                      </TableCell>
                      <TableCell component="th" scope="row" size="small" sx={{ padding: '.5rem !important' }}>
                        {file.size}
                      </TableCell>
                      <TableCell
                        component="th"
                        scope="row"
                        size="small"
                        sx={{ padding: '.5rem !important', color: isPendingAttachment(file) ? 'error.main' : 'success.main' }}
                      >
                        {isPendingAttachment(file) ? 'Uploading...' : 'Uploaded'}
                      </TableCell>
                      <TableCell align="right" size="small" sx={{ padding: '.5rem !important' }}>
                        <Button variant="contained" color="primary" onClick={() => handleDownload(file)}>
                          <GetAppIcon />
                        </Button>
                        {!onlyView && (
                          <Button
                            // className={reviseId && reviseId !== '0' ? 'non-editable-shadow' : ''}
                            variant="contained"
                            color="secondary"
                            style={{ marginLeft: '15px', width: '20px' }}
                            onClick={(e) => handleDelete(file)}
                          >
                            <DeleteOutlined />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      <DialogActions>
        <Button variant="contained" color="primary" onClick={handleClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default SupBankttachmentDialog
